import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { sha256 } from '@grandvisionhq/lib-utils-w3c';
import { useFeatureToggles, useSelector } from '@grandvisionhq/state';
import { getAuth } from '../selectors.js';
import { AuthState } from '../state.js';
import { useAccount } from './use-account.js';

/**
 * Hook that provides common user properties for analytics and tracking purposes.
 *
 * This hook returns a function that, when called, returns a promise resolving to user details
 * including login status and a hashed email (SHA256). The `authState` is used to determine whether
 * the user is logged in or a guest. If the user is logged out but an email is provided, it hashes
 * the provided email. The promise ensures accurate user data.
 *
 * @returns {Function} A function that accepts an optional email and returns a promise resolving to an object containing the user's login status.
 */ const useCommonUserProperties = ()=>{
    const authState = useAccount();
    const { isMyAccountEnabled  } = useFeatureToggles();
    const auth = useSelector(getAuth);
    return React.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(email) {
            // Some OpCos do not have MyA enabled so this ensures we return a default object
            // to prevent indefinitely waiting for the auth state (which will always be 'UNKNOWN').
            if (!isMyAccountEnabled) {
                return {
                    User_LoginStatus: 'Guest',
                    User_Email_SHA256: ''
                };
            }
            if (authState === 'UNKNOWN') return;
            let hashedEmail = '';
            const isUserLoggedIn = authState === AuthState.LoggedIn;
            if (isUserLoggedIn) {
                try {
                    hashedEmail = yield sha256(auth.email);
                } catch (error) {
                    console.error('Failed to hash email:', error);
                }
            } else if (email) {
                // In some scenarios we still want to send a hashed email to the Tealium datalayer,
                // even if a user is logged out/a guest. For example when signing up for a newsletter.
                try {
                    hashedEmail = yield sha256(email);
                } catch (error) {
                    console.error('Failed to hash email:', error);
                }
            }
            return {
                User_LoginStatus: isUserLoggedIn ? 'Logged' : 'Guest',
                User_Email_SHA256: hashedEmail
            };
        });
        return function(email) {
            return _ref.apply(this, arguments);
        };
    }(), [
        authState,
        isMyAccountEnabled,
        auth.email
    ]);
};

export { useCommonUserProperties };
//# sourceMappingURL=use-common-user-properties.js.map
